/**
 * EG Lore Character Detail
 * ------------------------
 * Wires card faces to their native <dialog> popups (via the theme's
 * <dialog-component> custom element in assets/dialog.js).
 *
 * Features:
 *   - Single popup open at a time (clicking a new card closes the previous).
 *   - aria-expanded reflects popup state on each card trigger.
 *   - Deep-linking: URL hash matches card id; history.pushState + popstate.
 *     Back button closes the popup; direct link auto-opens the card.
 *   - No dependency on Component base class; plain HTMLElement keeps footprint minimal.
 */

// Share completed/in-flight requests across cards and theme-editor section instances.
const loreStylesheets = new Map();

function loadLoreStylesheet(card) {
  const url = card.querySelector('[data-lore-stylesheet]')?.dataset.loreStylesheet;
  if (!url) return Promise.resolve(true);
  if (loreStylesheets.has(url)) return loreStylesheets.get(url);
  const request = new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    const finish = (loaded) => {
      clearTimeout(timeout);
      link.onload = link.onerror = null;
      if (!loaded) {
        link.remove();
        loreStylesheets.delete(url); // A later interaction can retry.
      }
      resolve(loaded);
    };
    const timeout = setTimeout(() => finish(false), 15000);
    link.onload = () => finish(true);
    link.onerror = () => finish(false);
    document.head.append(link);
  });
  loreStylesheets.set(url, request);
  return request;
}

class EgLoreCharacterDetail extends HTMLElement {
  #initialized = false;
  #cards = [];
  #mediaCleanup = [];
  #openRequest = 0;

  connectedCallback() {
    this.#initMedia();
    if (this.#initialized) return;
    this.#initialized = true;

    // Wait for <dialog-component> to register before wiring up.
    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('dialog-component').then(() => this.#init());
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    ++this.#openRequest;
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener('keydown', this.#handleEscape);
    this.#mediaCleanup.splice(0).forEach((cleanup) => cleanup());
  }

  #initMedia() {
    this.#mediaCleanup.splice(0).forEach((cleanup) => cleanup());
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const still = this.querySelector('.eg-lore-character__bg-image');
    const connection = navigator.connection;
    for (const video of this.querySelectorAll('[data-lore-video]')) {
      const viewport = window.matchMedia(video.dataset.media);
      let disposed = false;
      let loaded = false;
      const allowed = () => !disposed && viewport.matches && !reduced.matches &&
        !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '') && !document.hidden;
      const update = () => {
        if (!allowed()) {
          video.pause();
          delete video.dataset.playing;
          // Release an inactive viewport's video rather than continuing its download.
          if (loaded && (!viewport.matches || reduced.matches || connection?.saveData)) {
            video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
            video.load();
            loaded = false;
          }
          return;
        }
        if (still && !still.complete) return;
        if (!loaded) {
          video.querySelectorAll('source').forEach((source) => { source.src = source.dataset.src; });
          video.muted = true;
          video.load();
          loaded = true;
        }
        video.play().catch(() => {});
      };
      const playing = () => { if (allowed()) video.dataset.playing = 'true'; };
      video.addEventListener('playing', playing);
      viewport.addEventListener('change', update);
      reduced.addEventListener('change', update);
      connection?.addEventListener('change', update);
      document.addEventListener('visibilitychange', update);
      still?.addEventListener('load', update);
      still?.addEventListener('error', update);
      update();
      this.#mediaCleanup.push(() => {
        disposed = true;
        video.pause();
        video.removeEventListener('playing', playing);
        viewport.removeEventListener('change', update);
        reduced.removeEventListener('change', update);
        connection?.removeEventListener('change', update);
        document.removeEventListener('visibilitychange', update);
        still?.removeEventListener('load', update);
        still?.removeEventListener('error', update);
      });
    }
  }

  #init() {
    this.#cards = Array.from(this.querySelectorAll('[data-lore-card]'));

    for (const card of this.#cards) {
      const trigger = card.querySelector('[data-lore-card-trigger]');
      const dialogComp = card.querySelector('dialog-component');
      if (!trigger || !dialogComp) continue;

      const warmStyles = () => {
        const connection = navigator.connection;
        if (!connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '')) {
          loadLoreStylesheet(card);
        }
      };
      trigger.addEventListener('pointerenter', warmStyles);
      trigger.addEventListener('focus', warmStyles);
      trigger.addEventListener('pointerdown', warmStyles);
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.#openCard(card);
      });

      dialogComp.addEventListener('dialog:close', () => this.#onDialogClose(card));
    }

    // Auto-open on initial load if URL hash matches a card id.
    this.#applyHash();
    window.addEventListener('popstate', this.#handlePopState);
    document.addEventListener('keydown', this.#handleEscape);
  }

  #handleEscape = (event) => {
    if (event.key === 'Escape') {
      ++this.#openRequest;
      this.querySelectorAll('[aria-busy="true"]').forEach((el) => el.removeAttribute('aria-busy'));
    }
  };

  #handlePopState = () => {
    this.#applyHash();
  };

  #applyHash() {
    let id;
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
    const target = id ? this.#cards.find((c) => c.dataset.loreCard === id) : null;
    if (target) {
      this.#openCard(target, /* skipPushState */ true);
    } else {
      this.#closeAll();
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} [skipPushState]
   */
  async #openCard(card, skipPushState = false) {
    const request = ++this.#openRequest;

    const trigger = card.querySelector('[data-lore-card-trigger]');
    const dialogComp = card.querySelector('dialog-component');
    if (!dialogComp || typeof dialogComp.showDialog !== 'function') return;

    const dialog = dialogComp.querySelector('dialog');
    if (!dialog || dialog.open) {
      // Already open or missing — still sync aria.
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      return;
    }

    trigger?.setAttribute('aria-busy', 'true');
    const loaded = await loadLoreStylesheet(card);
    trigger?.removeAttribute('aria-busy');
    if (!loaded || request !== this.#openRequest || !this.isConnected) return;
    await this.#closeAllExcept(card);
    if (request !== this.#openRequest || !this.isConnected) return;
    // Native dialog restores focus to this trigger after closing, including deep links.
    trigger?.focus({ preventScroll: true });
    dialogComp.showDialog();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    const id = card.dataset.loreCard;
    if (!skipPushState && id) {
      const newUrl = `${window.location.pathname}${window.location.search}#${encodeURIComponent(id)}`;
      window.history.pushState({ loreCard: id }, '', newUrl);
    }
  }

  #closeAll() {
    ++this.#openRequest;
    for (const card of this.#cards) {
      this.#closeCard(card);
    }
  }

  async #closeAllExcept(exceptCard) {
    await Promise.all(this.#cards.filter((card) => card !== exceptCard).map((card) => this.#closeCard(card)));
  }

  async #closeCard(card) {
    const dialogComp = card.querySelector('dialog-component');
    const dialog = dialogComp && dialogComp.querySelector('dialog');
    if (dialog && dialog.open && typeof dialogComp.closeDialog === 'function') {
      await dialogComp.closeDialog();
    }
    const trigger = card.querySelector('[data-lore-card-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * Fired by <dialog-component> on ESC/backdrop/close-button close.
   * Pops the card id from the URL hash (without looping back through #applyHash).
   * @param {HTMLElement} card
   */
  #onDialogClose(card) {
    const trigger = card.querySelector('[data-lore-card-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    const currentHash = window.location.hash.slice(1);
    try {
      if (currentHash && decodeURIComponent(currentHash) === card.dataset.loreCard) {
        const cleanUrl = `${window.location.pathname}${window.location.search}`;
        window.history.pushState({}, '', cleanUrl);
      }
    } catch (_err) {
      /* malformed hash; leave it alone */
    }
  }
}

if (!window.customElements.get('eg-lore-character-detail')) {
  window.customElements.define('eg-lore-character-detail', EgLoreCharacterDetail);
}
